<template>
  <LoadingWrapper :state="status">
    <ObjectiveSlide
      :proposals="proposals"
      :running-objectives="runningObjectives"
      :agreed-objectives="agreedObjectives"
      :dialog-id="dialog.id"
      :feedback-topics="feedbackTopics"
      :creatable-objective="creatableObjective"
      :show-form="showForm"
      :is-read-only="isReadOnly"
      :are-impulses-read-only="areImpulsesReadOnly"
      :dialog-phase="dialog.phase"
      :can-create-objective="!!canEditObjectives"
      :can-cover="canCover"
      :covered-review-count="coveredReviewCount"
      :review-denominator="reviewDenominator"
      @create:objective="createObjective"
      @save:objective="saveObjective"
      @cancel:create="cancelCreateObjective"
    />
    <UnsavedChangesDialog
      :is-open="showConfirmDialog"
      @confirm="confirmLeave"
      @dismiss="cancelLeave"
    />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import ObjectiveSlide from './ObjectiveSlide.vue'
  import { useObjectives } from '@/core/composables/use-objectives'
  import { injectStrict } from '@/core/utils/inject-strict'
  import { computed, inject, watch } from 'vue'
  import {
    dialogKey,
    dialogRoleKey,
    feedbackTopicsKey,
  } from '../composable/use-dialog-navigation'
  import { DialogPhase } from '@/core/models/dialog/dialog'
  import { useUserStore } from '@/stores/user.store'
  import { useUnsavedChanges } from '@/core/composables/use-unsaved-changes'
  import UnsavedChangesDialog from '@/core/components/unsaved-changes-dialog/UnsavedChangesDialog.vue'

  const canEditObjectives = inject<boolean>('canEditObjectives')
  const dialog = injectStrict(dialogKey)
  const dialogRole = injectStrict(dialogRoleKey)
  const { user } = useUserStore()
  const isReadOnly = computed(() => dialog.phase === DialogPhase.Conducted)
  const areImpulsesReadOnly = computed(
    () =>
      isReadOnly.value ||
      (dialog.phase === DialogPhase.CoachPreparation && dialog.coachee.id === user.id),
  )
  const canCover = computed(
    () => dialogRole === 'coach' && dialog.phase === DialogPhase.Conversation,
  )
  const feedbackTopics = injectStrict(feedbackTopicsKey)
  const {
    proposals,
    runningObjectives,
    agreedObjectives,
    creatableObjective,
    createObjective,
    cancelCreateObjective,
    saveObjective,
    showForm,
    status,
    coveredReviewCount,
    reviewDenominator,
  } = useObjectives(
    () => ({
      dialogId: dialog.id,
      ownerId: dialog.coachee.id,
    }),
    { autoCoverCreates: true },
  )

  const { showConfirmDialog, confirmLeave, cancelLeave, setSourceDirty } = useUnsavedChanges()

  const createFormHasContent = computed(() => {
    if (!showForm.value) {
      return false
    }
    return !!(
      creatableObjective.title?.trim() ||
      creatableObjective.description?.trim() ||
      creatableObjective.feedbackTopicId
    )
  })

  watch(
    createFormHasContent,
    (dirty) => {
      setSourceDirty('create-form', dirty)
    },
    { immediate: true },
  )
</script>
